import React, { memo, useState, useCallback, useRef } from 'react';
import { Handle, Position, useReactFlow } from 'reactflow';
import CustomResizer from './CustomResizer';
import { Upload, message, Modal, Tooltip } from 'antd';
import { AppstoreOutlined, ExpandAltOutlined, CompressOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import api from '../../../api/client';
import NodeLabel from './NodeLabel';
import { useNodeTitle } from './useNodeTitle';
import MoodboardCanvas from './MoodboardCanvas';

const MoodboardNode = ({ id, data, selected }) => {
    const { projectId: canvasId } = useParams();
    const { setNodes } = useReactFlow();
    const [uploading, setUploading] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const nodeRef = useRef(null);
    const onTitleChange = useNodeTitle(id, data);

    // Initial data
    const mediaItems = data.mediaItems || [];
    // Use darker background similar to TextNode container or transparent root with styled container
    const backgroundColor = data.backgroundColor || '#161b22';

    // Separate local state update from DB persistence to prevent API flooding during drag
    const updateLocalState = useCallback((newData) => {
        setNodes((nds) => nds.map((node) => {
            if (node.id === id) {
                return {
                    ...node,
                    data: {
                        ...node.data,
                        ...newData
                    }
                };
            }
            return node;
        }));
    }, [id, setNodes]);

    const saveToDb = useCallback((newData) => {
        if (data.onSave) {
            data.onSave(id, newData);
        }
    }, [id, data]);

    const handleLocalItemsChange = (updatedItems) => {
        updateLocalState({ mediaItems: updatedItems });
    };

    const handleSaveItems = (updatedItems) => {
        saveToDb({ mediaItems: updatedItems });
    };

    const handleDropFiles = async (files, { x, y }) => {
        setUploading(true);
        let dropX = x;
        let dropY = y;

        try {
            const newItems = [];
            for (const file of files) {
                if (!file.type.startsWith('image/')) continue;

                const formData = new FormData();
                formData.append('file', file);
                formData.append('bucket', 'media-uploads');

                const response = await api.post('/storage/upload', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });

                if (response.data && response.data.url) {
                    const newItem = {
                        id: crypto.randomUUID(),
                        type: 'image',
                        src: response.data.url,
                        x: dropX,
                        y: dropY,
                        width: 150,
                        height: 150, // This will likely be auto-adjusted by img aspect ratio visually, but good to have base
                        rotation: (Math.random() * 20) - 10,
                        zIndex: mediaItems.length + newItems.length + 1
                    };

                    newItems.push(newItem);

                    // Persist individual item addition for granularity if needed, 
                    // though updateData handles the full list save usually.
                    // If backend expects specific "add" endpoint:
                    await api.post(`/nodes/${id}/media`, newItem);

                    dropX += 20;
                    dropY += 20;
                }
            }

            if (newItems.length > 0) {
                const updatedList = [...mediaItems, ...newItems];
                updateLocalState({ mediaItems: updatedList });
                saveToDb({ mediaItems: updatedList });
                message.success('Media added to moodboard');
            }
        } catch (error) {
            console.error('Upload failed:', error);
            message.error('Failed to upload media');
        } finally {
            setUploading(false);
        }
    };


    return (
        <div
            ref={nodeRef}
            style={{
                position: 'relative',
                height: '100%',
                width: '100%',
                borderRadius: 8,
                boxShadow: selected ? '0 0 0 2px #1890ff' : '0 4px 12px rgba(0,0,0,0.15)',
            }}
        >
            <CustomResizer
                isVisible={selected}
                minWidth={300}
                minHeight={300}
                onResizeEnd={(event, params) => {
                    const { width, height, x, y } = params;
                    if (data.onResize) {
                        data.onResize(id, { width, height, x, y });
                    }
                }}
            />

            <NodeLabel
                title={data.nodeTitle || 'Moodboard'}
                iconType={data.iconType || 'appstore'}
                color={data.nodeColor || '#ffec3d'}
                onTitleChange={onTitleChange}
                // Right side extra controls
                extra={
                    <Tooltip title="Expand View">
                        <ExpandAltOutlined
                            className="nodrag"
                            onClick={() => setIsExpanded(true)}
                            style={{ cursor: 'pointer', color: '#8b949e', fontSize: 16, marginLeft: 8 }}
                        />
                    </Tooltip>
                }
            />

            {/* Content Container */}
            <div style={{
                background: backgroundColor,
                borderRadius: 8,
                border: '1px solid #30363d',
                boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                width: '100%',
                height: '100%',
                position: 'relative',
                overflow: 'hidden'
            }}>
                <MoodboardCanvas
                    items={mediaItems}
                    onItemsChange={handleLocalItemsChange}
                    onSave={handleSaveItems}
                    onDropFiles={handleDropFiles}
                    isUploadActive={uploading}
                    readOnly={false}
                />
            </div>

            {/* Right Handle */}
            <Handle
                type="source"
                position={Position.Right}
                style={{
                    width: 150, height: 150, right: -180, top: 120,
                    borderRadius: '50%',
                    background: data.nodeColor || '#ffec3d', border: '12px solid #0d1117',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 12px 36px rgba(0,0,0,0.5)'
                }}
            >
                <AppstoreOutlined style={{ color: '#fff', fontSize: 72, pointerEvents: 'none' }} />
            </Handle>

            {/* Expanded Modal */}
            <Modal
                title={null}
                footer={null}
                open={isExpanded}
                onCancel={() => setIsExpanded(false)}
                width="90vw"
                style={{ top: 20 }}
                bodyStyle={{ height: '85vh', padding: 0, overflow: 'hidden', background: '#0d1117' }}
                closeIcon={<span style={{ color: '#fff' }}>✕</span>}
            >
                <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {/* Header for Modal */}
                    <div style={{ padding: '12px 24px', borderBottom: '1px solid #30363d', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#161b22' }}>
                        <span style={{ color: '#fff', fontWeight: 'bold' }}>{data.nodeTitle || 'Moodboard'} - Expanded</span>
                    </div>

                    <div style={{ flex: 1, position: 'relative', backgroundColor: backgroundColor }}>
                        <MoodboardCanvas
                            items={mediaItems}
                            onItemsChange={handleLocalItemsChange}
                            onSave={handleSaveItems}
                            onDropFiles={handleDropFiles}
                            isUploadActive={uploading}
                            readOnly={false}
                        />
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default memo(MoodboardNode);
